/*  
<summary>
  Functional Component: CategoryList
  Developer: Mohammad Saquib Khan
  Created Date: 06-Jan-2025
  Description: Renders a staff management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Staff Management interface</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import { useStore } from "../../../contexts/store-provider";
import IPageInfo, { IPageLimitInfo } from "../../../models/ICommon";
import LoaderButton from "../../../shared-components/button/loader-button";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import AddCategoryManagement from "./components/add-edit-category";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { ActionType } from "../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { isOperationPermittedRoleBase } from "../../../helpers/action-permission-helper";
import {
  getBusinessTypeIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import "./category-list.css";

const CategoryList: React.FC = (props: any) => {
  const {
    categoryManagementStore,
    preferencesStore,
    authStore,
    masterManagementStore,
  } = useStore();
  const [openPanel, setOpenPanel] = useState(false);
  const {
    inProgress,
    error,
    GetCategoryManagementListService,
    GetCategoryManagementListFilterService,
    allCategoryManagement,
    categoryList,
    GetSearchCategoryFilterService,
    resetAddUpdateCategoryManagementState,
    addUpdateCategoryManagementState,
    deleteCategoryManagementState,
    DeleteCategoryManagementService,
    resetDeleteCategoryManagement,
    UpdateCategoryOrderIdService,
    resetCategoryManagementDetail,
    getCategoryFilter,
    setCategoryFilterDetail,
    resetCategoryFilterDetail,
  } = categoryManagementStore;
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const { GetAllBusinessTypeService, getAllBusinessTypes } =
    masterManagementStore;
  const [showInvalidStores, setShowInvalidStores] = useState(false);
  const [distinction, setDistinction] = useState<number>(1);
  const [businessType, setBusinessType] = useState<number>(1);
  const [orderId, setOrderId] = useState<number>(0);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [selectedPath, setSelectedPath] = useState<string>("");
  const [selectedDetails, setSelectedDetails] = useState<any>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [treeData, setTreeData] = useState<any[]>(allCategoryManagement);
  const [freeWordSearch, setFreeWordSearch] = useState("");
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const userType = getUserTypeFromLocalStorage();
  const businessTypeId = getBusinessTypeIdFromLocalStorage();

  const getAllKeys = (data: any) => {
    let keys: any[] = [];
    data.forEach((item: any) => {
      keys.push(item.key);
      if (item.children) {
        keys = keys.concat(getAllKeys(item.children));
      }
    });
    return keys;
  };

  const [expandedKeys, setExpandedKeys] = useState(getAllKeys(treeData));
  const handleDistinction = (event: any) => {
    event.target.value = event.target.value;
    setFreeWordSearch("");
    setDistinction(Number(event.target.value));
  };

  const handleBusinessType = (event: any) => {
    setFreeWordSearch("");
    event.target.value = event.target.value;
    setBusinessType(Number(event.target.value));
  };

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const handleSearchClick = () => {
    const searchQuery = freeWordSearch.trim();

    if (searchQuery?.length === 0) {
      // If search is empty, load all data again
      resetCategoryFilterDetail();
      callGetCategoryManagementListService();
      return;
    }

    setCategoryFilterDetail({
      ...getCategoryFilter,
      IsFilterActive: true,
    });
    // if (searchQuery.length < 3) {
    //   toast.error(formatMessage("search_word_minimum_length"));
    //   return;
    // }

    // Fetch filtered data based on search input
    GetSearchCategoryFilterService(searchQuery, distinction, businessType);
  };

  /**
   * This function calls the "getAllGroup" staff funtion that get configuration list from API
   */
  const callGetCategoryManagementListService = () => {
    if (userType === userTypeEnum[userTypeEnum.HeadQuarter]) {
      if (businessTypeId !== businessType) {
        setBusinessType(businessTypeId);
        return;
      }
    }

    GetCategoryManagementListFilterService(businessType, distinction);
  };

  useEffect(() => {
    if (allCategoryManagement?.length > 0) {
      setTreeData(allCategoryManagement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategoryManagement]);

  useEffect(() => {
    if (categoryList.CategoryVM?.length > 0) {
      const allKeys = getAllKeys(treeData); // Extract all keys
      setExpandedKeys(allKeys);
      setTreeData(allCategoryManagement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryList]);

  useEffect(() => {
    if (businessType > -1 && distinction > -1) {
      callGetCategoryManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessType, distinction]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetCategoryManagementListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCategoryManagementState.success) {
      resetAddUpdateCategoryManagementState();
      callGetCategoryManagementListService();
      setTreeData(allCategoryManagement);
      setIsEdit(false);
      setSelectedId(-1);
      setSelectedPath("");
      setSelectedDetails([]);
      setOrderId(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateCategoryManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteCategoryManagementState.success) {
      toast.success(formatMessage("deleted_success"));
      props.deletePopupToggleHandler();
      resetDeleteCategoryManagement();
      callGetCategoryManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCategoryManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteCategoryManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteCategoryManagementState.error) {
      toast.error(formatMessage(deleteCategoryManagementState.error));
      props.deletePopupToggleHandler();
      resetDeleteCategoryManagement();
    }
  }, [deleteCategoryManagementState.error]);

  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddCategoryManagementHandler = (row: { Id: number }) => {
    setOrderId(allCategoryManagement?.length + 1);
    setSelectedId(row.Id);
    props.addToggleHandler();
  };

  const openAddCategoryMainNodeHandler = (Id: number) => {
    setOrderId(allCategoryManagement?.length + 1);
    setIsEdit(false);
    setSelectedId(Id);
    setSelectedPath("");
    setSelectedDetails([]);
    // setOrderId(0);
    props.addToggleHandler();
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetCategoryManagementListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This function deletes the user by providing Id to the Api
   */
  const deleteHandler = () => {
    DeleteCategoryManagementService(selectedId);
  };

  /**
   * This function shows the delete user confirmation popup component and sets the seleted user Id
   */
  const deleteClickHandler = (Id: number) => {
    setSelectedId(Id);
    props.deletePopupToggleHandler();
  };

  /**
   * This function provides manual refresh functionality
   */
  const refreshClickHandler = () => {
    callGetCategoryManagementListService();
  };

  useEffect(() => {
    GetAllBusinessTypeService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCheck = (keys: any) => {
    setCheckedKeys(keys);
  };

  const renderTitle = (node: any, path: string[], details: number[]) => (
    <div className="d-flex align-items-center gap-3 justify-content-between">
      <span className="text-wrap">
        {node.title}{" "}
        <span className="ms-2">{node.desc ? "(" + node.desc + ")" : ""}</span>
      </span>
      {/* <span>{node.desc}</span> */}
      {isOperationPermittedRoleBase(
        ModuleType.CategoryManagement,
        ActionType.Add
      ) && (
        <div className="node-collection d-flex align-items-center gap-3">
          <button
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px",
            }}
          >
            <i className="bi bi-arrow-down-up"></i>
          </button>
          <button
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px",
            }}
            onClick={(e) => {
              setIsEdit(true);
              setSelectedId(node.key);
              setSelectedPath(path.join("/"));
              setSelectedDetails(details);
              setOrderId(node?.children?.length);
              props.addToggleHandler();
              e.stopPropagation(); // Prevent triggering the tree's default events
            }}
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px",
            }}
            onClick={(e: any) => {
              setIsEdit(false);
              setOrderId(node?.children?.length + 1);
              setSelectedId(node.key);
              setSelectedPath(path.join("/"));
              setSelectedDetails(details);
              props.addToggleHandler();
              e.stopPropagation(); // Prevent triggering the tree's default events
            }}
          >
            <i className="bi bi-plus-lg"></i>
          </button>

          <button
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "2px",
            }}
            onClick={(e: any) => {
              setIsEdit(false);
              setOrderId(node?.children?.length + 1);
              setSelectedId(node.key);
              setSelectedPath(path.join("/"));
              deleteClickHandler(node.key);
              e.stopPropagation(); // Prevent triggering the tree's default events
            }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      )}
    </div>
  );

  const onDrop = (info: any) => {
    const dragKey = info.dragNode.key; // Key of the node being dragged
    const dropKey = info.node.key; // Key of the node where the item is dropped
    const dragKeyObj = findObjByKey(treeData, dragKey);
    const dropKeyObj = findObjByKey(treeData, dropKey);
    const dragOrdId = dragKeyObj?.orderId;
    const dropOrdId = dropKeyObj?.orderId;
    const dragParentOrdId = dragKeyObj?.parentOrgId;
    const dropParentOrdId = dropKeyObj?.parentOrgId;

    if (
      dragParentOrdId == dropParentOrdId &&
      dragParentOrdId + dropParentOrdId == 0
    ) {
      if (dragOrdId && dropOrdId && dragOrdId > dropOrdId) {
        const changesOrdersArray: any[] = [];
        const updatesForDragnDrop: any[] = [];
        for (let i = dropOrdId; i < dragOrdId; i++) {
          const data = treeData.find((item: any) => item.orderId === i);
          if (data) {
            changesOrdersArray.push({
              ordId: i,
              key: data.key,
            });
          }
        }
        updatesForDragnDrop.push({
          Id: dragKey,
          OrderId: dropKeyObj.orderId,
        });
        const filteredTreeData = treeData.filter(
          (item) => item.key !== dragKey
        );
        filteredTreeData.map((item) => {
          const found = changesOrdersArray.find(
            (order) => order.ordId === item.orderId && order.key === item.key
          );
          if (found) {
            updatesForDragnDrop.push({
              Id: item.key,
              OrderId: item.orderId + 1,
            });
          }
        });
        UpdateCategoryOrderIdService({ Categories: updatesForDragnDrop });
      } else if (dragOrdId && dropOrdId && dropOrdId > dragOrdId) {
        const changesOrdersArray: any[] = [];
        const updatesForDragnDrop: any[] = [];
        for (let i = dropOrdId; i > dragOrdId; i--) {
          const data = treeData.find((item: any) => item.orderId === i);
          if (data) {
            changesOrdersArray.push({
              ordId: i,
              key: data.key,
            });
          }
        }
        updatesForDragnDrop.push({
          Id: dragKey,
          OrderId: dropKeyObj.orderId,
        });
        const filteredTreeData = treeData.filter(
          (item) => item.key !== dragKey
        );
        filteredTreeData.map((item) => {
          const found = changesOrdersArray.find(
            (order) => order.ordId === item.orderId && order.key === item.key
          );
          if (found) {
            updatesForDragnDrop.push({
              Id: item.key,
              OrderId: item.orderId - 1,
            });
          }
        });
        UpdateCategoryOrderIdService({ Categories: updatesForDragnDrop });
      }
    } else if (
      dragParentOrdId == dropParentOrdId &&
      dragParentOrdId + dropParentOrdId > 0
    ) {
      // This code will work for Bottom - to - Top Approach for Children Node Drag & Drop
      if (dragOrdId && dropOrdId && dragOrdId > dropOrdId) {
        const changesOrdersArray: any[] = [];
        const updatesForDragnDrop: any[] = [];
        const childrenArray = findObjByKey(treeData, dragParentOrdId)?.children;
        for (let i = dropOrdId; i < dragOrdId; i++) {
          const data = childrenArray.find((item: any) => item.orderId === i);
          if (data) {
            changesOrdersArray.push({
              ordId: i,
              key: data.key,
            });
          }
        }

        const dragKeyObject = childrenArray?.find(
          (item: any) => item.key == dragKey
        );
        const dropKeyObject = childrenArray?.find(
          (item: any) => item.key == dropKey
        );
        updatesForDragnDrop.push({
          Id: dragKeyObject.key,
          OrderId: dropKeyObject.orderId,
        });
        const filteredData = childrenArray.filter(
          (item: any) => item.key !== dragKey
        );
        filteredData?.map((item: any) => {
          const found = changesOrdersArray?.find(
            (order: any) =>
              order.ordId === item.orderId && order.key === item.key
          );
          if (found) {
            updatesForDragnDrop.push({
              Id: item.key,
              OrderId: item.orderId + 1,
            });
          }
        });
        UpdateCategoryOrderIdService({ Categories: updatesForDragnDrop });
      }

      // This code will work for Top - to - Bottom Approach for Children Node Drag & Drop
      else if (dragOrdId && dropOrdId && dropOrdId > dragOrdId) {
        const changesOrdersArray: any[] = [];
        const updatesForDragnDrop: any[] = [];
        const childrenArray = findObjByKey(treeData, dragParentOrdId)?.children;
        for (let i = dropOrdId; i > dragOrdId; i--) {
          const data = childrenArray.find((item: any) => item.orderId === i);
          if (data) {
            changesOrdersArray.push({
              ordId: i,
              key: data.key,
            });
          }
        }

        const dragKeyObject = childrenArray?.find(
          (item: any) => item.key == dragKey
        );
        const dropKeyObject = childrenArray?.find(
          (item: any) => item.key == dropKey
        );
        updatesForDragnDrop.push({
          Id: dragKeyObject.key,
          OrderId: dropKeyObject.orderId,
        });
        const filteredData = childrenArray.filter(
          (item: any) => item.key !== dragKey
        );
        filteredData?.map((item: any) => {
          const found = changesOrdersArray?.find(
            (order: any) =>
              order.ordId === item.orderId && order.key === item.key
          );
          if (found) {
            updatesForDragnDrop.push({
              Id: item.key,
              OrderId: item.orderId - 1,
            });
          }
        });
        UpdateCategoryOrderIdService({ Categories: updatesForDragnDrop });
      }
    }
  };

  const replaceChildren = (data: any, key: any, newChildren: any) => {
    return data.map((item: any) => {
      if (item.key === key) {
        return { ...item, children: newChildren };
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: replaceChildren(item.children, key, newChildren),
        };
      }
      return item;
    });
  };

  const findObjByKey = (data: any, key: number): any => {
    for (const obj of data) {
      if (obj.key === key) {
        return obj;
      }

      if (obj.children && obj.children.length > 0) {
        const found = findObjByKey(obj.children, key);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const findObjByOrderId = (data: any, orderId: number): any => {
    for (const obj of data) {
      if (obj.orderId === orderId) {
        return obj;
      }

      if (obj.children && obj.children.length > 0) {
        const found = findObjByOrderId(obj.children, orderId);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const processTreeData = (
    nodes: any[],
    currentPath: string[] = [],
    detailsArray: number[] = []
  ): any[] => {
    return nodes
      .sort((a, b) => (a.orderId || 0) - (b.orderId || 0)) // Sort by orderId
      .map((node) => {
        // Build the path for the current node
        const path = [...currentPath, node.title];
        const details: any[] = [...detailsArray, node.key];

        return {
          ...node,
          title: renderTitle(node, path, details), // Pass the correct path to renderTitle
          children: node.children
            ? processTreeData(node.children, path, details)
            : [], // Recursively process children with updated path
        };
      });
  };

  return (
    <React.Fragment>
      {inProgress && <FormLoader loading={inProgress} />}

      {props.showAddPopup && (
        <AddCategoryManagement
          id={selectedId}
          editMode={isEdit}
          distinction={distinction}
          businessType={businessType}
          selectedPath={selectedPath}
          selectedDetails={selectedDetails}
          orderLength={orderId}
          modalClosed={props.addToggleHandler}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_category"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_category_confirm"
          isLoading={deleteCategoryManagementState.inProgress}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="category_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="master_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="category_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row mb-3 align-items-center">
          <div className="col-lg-9 search_box">
            <div className="row">
              <div className="col-md-4">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleSearchClick}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_free_word")}
                    value={freeWordSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFreeWordSearch(value);

                      if (value.trim().length === 0) {
                        // Reload all data when input is cleared
                        resetCategoryFilterDetail();
                        callGetCategoryManagementListService();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="input-group">
                  <div className="input-group-text">
                    {/* <Label
                      className="label-style"
                      required={false}
                      label="distinction"
                    /> */}
                  </div>
                  <select
                    id="options"
                    value={distinction}
                    className="form-control"
                    onChange={handleDistinction}
                  >
                    {/* <option value="-1" disabled>
                      -- {formatMessage("select_an_option")} --
                    </option> */}
                    <option value="1">{formatMessage("treatment")}</option>
                    <option value="2">{formatMessage("merchandise")}</option>
                  </select>
                </div>
              </div>
              {userType !== userTypeEnum[userTypeEnum.HeadQuarter] && (
                <div className="col-md-4">
                  <div className="input-group">
                    <div className="input-group-text">
                      {/* <Label
                      className="label-style"
                      required={false}
                      label="business_type"
                    /> */}
                    </div>
                    <select
                      id="options"
                      value={businessType}
                      className="form-control"
                      onChange={handleBusinessType}
                      disabled={
                        userType === userTypeEnum[userTypeEnum.HeadQuarter]
                      }
                    >
                      {/* <option value="-1" disabled>
                      --{formatMessage("select_an_option")}--
                    </option> */}
                      {getAllBusinessTypes
                        .filter((option) => option.id !== -1)
                        .map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-lg-3">
            {isOperationPermittedRoleBase(
              ModuleType.CategoryManagement,
              ActionType.Add
            ) && (
              <div className="d-flex justify-content-end align-items-center">
                {getCategoryFilter.IsFilterActive && (
                  <button
                    className={`btn ${
                      getCategoryFilter.IsFilterActive
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => {
                      resetCategoryFilterDetail();
                      setFreeWordSearch("");
                      callGetCategoryManagementListService();
                    }}
                    title={formatMessage(
                      getCategoryFilter.IsFilterActive
                        ? "clear_filter"
                        : "apply_filter"
                    )}
                  >
                    <svg
                      id="fi_3126668"
                      enableBackground="new 0 0 512 512"
                      height="20"
                      viewBox="0 0 512 512"
                      width="20"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#ffffff"
                    >
                      <g>
                        <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                        <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                      </g>
                    </svg>
                  </button>
                )}
                {distinction > -1 && businessType > -1 && (
                  <button
                    className="btn btn-primary ms-3"
                    onClick={() => {
                      openAddCategoryMainNodeHandler(0);
                    }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    {formatMessage("add_category")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {allCategoryManagement.length > 0 ? (
          <div className={`${openPanel ? "panelOpen" : ""}`}>
            <div>
              <div className="card changingPanel">
                <div className="tableDesign row-eq-height tableDevices">
                  <div className="table-responsive" data-testid="table">
                    <React.Fragment>
                      <Tree
                        checkable={false}
                        onCheck={onCheck}
                        draggable={isOperationPermittedRoleBase(
                          ModuleType.CategoryManagement,
                          ActionType.Add
                        )}
                        treeData={processTreeData(treeData)}
                        onDrop={onDrop}
                        checkedKeys={checkedKeys}
                        defaultExpandAll={true}
                        defaultExpandParent={true}
                        expandedKeys={expandedKeys} // Force expansion
                        onExpand={(keys) => setExpandedKeys(keys)} // Handle user collapse/expand
                      />
                    </React.Fragment>
                    <div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="message text-center">
            <img src={noMessageIcon} alt="" className="no_message" />
            {freeWordSearch.trim().length > 0 ? (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_data_available" />
                </p>
              </>
            ) : (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_category" />
                </p>
                <p className="mb-3">{formatMessage("no_category_message")}</p>
                {isOperationPermittedRoleBase(
                  ModuleType.CategoryManagement,
                  ActionType.Add
                ) && (
                  <>
                    {distinction > -1 && businessType > -1 && (
                      <LoaderButton
                        text="add_category"
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                          openAddCategoryManagementHandler({ Id: 0 })
                        }
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(CategoryList)))
);
